"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import CountryDropdown from "./CountryDropdown";
import Cookies from "js-cookie";
import "@/styles/components/navbar.scss";

// ─── Shared Locale Content ────────────────────────────────────────────────────
// Single source of truth — mirrors footer.jsx getLocaleContent() structure.
// Add new languages here; both Navbar and Footer will pick them up automatically.

const getLocaleContent = (locale) => {
  switch (locale) {
    case "ar-IQ":
      return {
        navGroups: {
          platform: "المنصة",
          resource: "الموارد",
          carriers: "الناقلون",
          pricing: "التسعير",
        },
        sections: {
          company: "الشركة",
          app: "التطبيق",
          learn: "تعلم",
          help: "المساعدة",
          shipping: "الشحن",
          payments: "المدفوعات",
          plans: "الخطط",
          legal: "قانوني",
        },
        // Reused from footer exploreLinks
        exploreLinks: {
          main: {
            left: [
              { title: "تحميل تطبيق Baskitt", href: "/#hero_section" },
              { title: "لماذا نحن", href: "/#features_section" },
              { title: "معلومات عنا", href: "/#about_us_section" },
              { title: "الأكثر استخدامًا", href: "/#most_use_section" },
              { title: "الميزات الرئيسية", href: "/#key_features_section" },
            ],
            right: [
              { title: "الشهادات", href: "/#testimonials" },
              { title: "دروس تعليمية", href: "/#product_tutorials" },
              { title: "التكامل", href: "/#integration" },
              { title: "المناطق", href: "/#located_section" },
              { title: "اتصل بنا", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "تحميل تطبيق Baskitt", href: "/#hero_section" },
              { title: "لماذا نحن", href: "/#why_us_section" },
              { title: "حاسبة الشحن", href: "/#shipping_calculator" },
              { title: "مواقعنا", href: "/#select_locations" },
              { title: "ميزات التطبيق", href: "/#app_features_section" },
            ],
            right: [
              { title: "كيف يعمل", href: "/#how_it_works_section" },
              { title: "الميزات الرئيسية", href: "/#features_section" },
              { title: "طرق الدفع", href: "/#payment_methods_section" },
              { title: "اتصل بنا", href: "/#contact-us" },
            ],
          },
        },
        legalLinks: [
          { title: "سياسة الخصوصية", href: "/privacy-policy" },
          { title: "شروط الاستخدام", href: "/terms-of-use" },
          { title: "سياسة الاسترداد", href: "/refund-policy" },
          { title: "سياسة حذف العملاء", href: "/customer-deletion-policy" },
        ],
        resourceLinks: [
          { title: "دروس الفيديو", href: "/tutorials" },
          { title: "الشهادات", href: "/#testimonials" },
          { title: "الأسئلة الشائعة", href: "/faqs" },
          { title: "المدونات", href: "/blogs" },
        ],
        helpLinks: [
          { title: "اتصل بنا", href: "/#contact-us" },
          { title: "مناطقنا", href: "/#located_section" },
        ],
        shippingLinks: [
          { title: "حاسبة الشحن", href: "/#shipping_calculator" },
          { title: "مواقعنا", href: "/#select_locations" },
          { title: "لا يمكن شحنه", href: "/shipping-restrictions" },
        ],
        paymentLinks: [
          { title: "طرق الدفع", href: "/#payment_methods_section" },
          { title: "التكامل", href: "/#integration" },
        ],
        planLinks: [
          { title: "كيف يعمل", href: "/#how_it_works_section" },
          { title: "الميزات", href: "/#features_section" },
        ],
      };

    case "ckb":
      return {
        navGroups: {
          platform: "پلاتفۆرم",
          resource: "سەرچاوە",
          carriers: "گەیاندنەکان",
          pricing: "نرخ",
        },
        sections: {
          company: "کۆمپانیا",
          app: "ئەپ",
          learn: "فێربوون",
          help: "یارمەتی",
          shipping: "گواستنەوە",
          payments: "پارەدانەکان",
          plans: "پلانەکان",
          legal: "یاسایی",
        },
        exploreLinks: {
          main: {
            left: [
              { title: "داونلۆدی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#features_section" },
              { title: "دەربارەی ئێمە", href: "/#about_us_section" },
              { title: "زۆرترین بەکارهێنان", href: "/#most_use_section" },
              {
                title: "تایبەتمەندییە سەرەکیەکان",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "شایەتیەکان", href: "/#testimonials" },
              { title: "وانەکان", href: "/#product_tutorials" },
              { title: "یەکگرتن", href: "/#integration" },
              { title: "ناوچەکان", href: "/#located_section" },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "داونلۆدی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#why_us_section" },
              { title: "حیساب کردنی گەیاندن", href: "/#shipping_calculator" },
              { title: "شوێنەکانمان", href: "/#select_locations" },
              {
                title: "تایبەتمەندییەکانی ئەپ",
                href: "/#app_features_section",
              },
            ],
            right: [
              { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
              { title: "تایبەتمەندییە سەرەکیەکان", href: "/#features_section" },
              {
                title: "شێوازەکانی پارەدان",
                href: "/#payment_methods_section",
              },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
        },
        legalLinks: [
          { title: "سیاسەتی تایبەتمەندی", href: "/privacy-policy" },
          { title: "مەرجەکانی بەکارهێنان", href: "/terms-of-use" },
          { title: "سیاسەتی گەڕاندنەوەی پارە", href: "/refund-policy" },
          {
            title: "سیاسەتی سڕینەوەی کڕیار",
            href: "/customer-deletion-policy",
          },
        ],
        resourceLinks: [
          { title: "وانە ڤیدیۆییەکان", href: "/tutorials" },
          { title: "پرسیارە باوەکان", href: "/faqs" },
          { title: "بلۆگەکان", href: "/blogs" },
        ],
        helpLinks: [
          { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
          { title: "ناوچەکانمان", href: "/#located_section" },
        ],
        shippingLinks: [
          { title: "حیساب کردنی گەیاندن", href: "/#shipping_calculator" },
          { title: "شوێنەکانمان", href: "/#select_locations" },
          { title: "ناتوانرێت بنێردرێت", href: "/shipping-restrictions" },
        ],
        paymentLinks: [
          { title: "شێوازەکانی پارەدان", href: "/#payment_methods_section" },
          { title: "یەکگرتن", href: "/#integration" },
        ],
        planLinks: [
          { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
          { title: "تایبەتمەندییەکان", href: "/#features_section" },
        ],
      };

    case "sdh":
      return {
        navGroups: {
          platform: "پلاتفۆرم",
          resource: "سەرچاوە",
          carriers: "گەیاندنەکان",
          pricing: "نرخ",
        },
        sections: {
          company: "کۆمپانیا",
          app: "ئەپ",
          learn: "فێربوون",
          help: "یارمەتی",
          shipping: "گواستنەوە",
          payments: "پارەدانەکان",
          plans: "پلانەکان",
          legal: "یاسایی",
        },
        exploreLinks: {
          main: {
            left: [
              { title: "داگرتنی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#features_section" },
              { title: "دەربارەی ئێمە", href: "/#about_us_section" },
              { title: "زۆرترین بەکارهێنان", href: "/#most_use_section" },
              {
                title: "تایبەتمەندییە سەرەکیەکان",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "شایەتیەکان", href: "/#testimonials" },
              { title: "فێرکاریەکان", href: "/#product_tutorials" },
              { title: "یەکگرتن", href: "/#integration" },
              { title: "ناوچەکان", href: "/#located_section" },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "داگرتنی ئەپی Baskitt", href: "/#hero_section" },
              { title: "بۆچی ئێمە", href: "/#why_us_section" },
              { title: "ژمێرەری گواستنەوە", href: "/#shipping_calculator" },
              { title: "شوێنەکانمان", href: "/#select_locations" },
              {
                title: "تایبەتمەندییەکانی ئەپ",
                href: "/#app_features_section",
              },
            ],
            right: [
              { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
              { title: "تایبەتمەندییە سەرەکیەکان", href: "/#features_section" },
              {
                title: "شێوازەکانی پارەدان",
                href: "/#payment_methods_section",
              },
              { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
            ],
          },
        },
        legalLinks: [
          { title: "سیاسەتی تایبەتمەندی", href: "/privacy-policy" },
          { title: "مەرجەکانی بەکارهێنان", href: "/terms-of-use" },
          { title: "سیاسەتی گەڕاندنەوەی پارە", href: "/refund-policy" },
          {
            title: "سیاسەتی سڕینەوەی کڕیار",
            href: "/customer-deletion-policy",
          },
        ],
        resourceLinks: [
          { title: "فێرکاریە ڤیدیۆییەکان", href: "/tutorials" },
          { title: "پرسیارە باوەکان", href: "/faqs" },
          { title: "بلۆگ", href: "/blogs" },
        ],
        helpLinks: [
          { title: "پەیوەندیمان پێوە بکە", href: "/#contact-us" },
          { title: "ناوچەکانمان", href: "/#located_section" },
        ],
        shippingLinks: [
          { title: "ژمێرەری گواستنەوە", href: "/#shipping_calculator" },
          { title: "شوێنەکانمان", href: "/#select_locations" },
          { title: "ناتوانرێت بنێردرێت", href: "/shipping-restrictions" },
        ],
        paymentLinks: [
          { title: "شێوازەکانی پارەدان", href: "/#payment_methods_section" },
          { title: "یەکگرتن", href: "/#integration" },
        ],
        planLinks: [
          { title: "چۆن کار دەکات", href: "/#how_it_works_section" },
          { title: "تایبەتمەندییەکان", href: "/#features_section" },
        ],
      };

    case "es":
      return {
        navGroups: {
          platform: "Plataforma",
          resource: "Recursos",
          carriers: "Transportistas",
          pricing: "Precios",
        },
        sections: {
          company: "Empresa",
          app: "App",
          learn: "Aprender",
          help: "Ayuda",
          shipping: "Envío",
          payments: "Pagos",
          plans: "Planes",
          legal: "Legal",
        },
        exploreLinks: {
          main: {
            left: [
              { title: "Descargar App Baskitt", href: "/#hero_section" },
              { title: "Por Qué Nosotros", href: "/#features_section" },
              { title: "Sobre Nosotros", href: "/#about_us_section" },
              { title: "Más Usado", href: "/#most_use_section" },
              {
                title: "Características Clave",
                href: "/#key_features_section",
              },
            ],
            right: [
              { title: "Testimonios", href: "/#testimonials" },
              { title: "Tutoriales", href: "/#product_tutorials" },
              { title: "Integración", href: "/#integration" },
              { title: "Zonas", href: "/#located_section" },
              { title: "Contáctenos", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "Descargar App Baskitt", href: "/#hero_section" },
              { title: "Por Qué Nosotros", href: "/#why_us_section" },
              { title: "Calculadora de Envío", href: "/#shipping_calculator" },
              { title: "Nuestras Ubicaciones", href: "/#select_locations" },
              { title: "Funciones de la App", href: "/#app_features_section" },
            ],
            right: [
              { title: "Cómo Funciona", href: "/#how_it_works_section" },
              { title: "Características Clave", href: "/#features_section" },
              { title: "Métodos de Pago", href: "/#payment_methods_section" },
              { title: "Contáctenos", href: "/#contact-us" },
            ],
          },
        },
        legalLinks: [
          { title: "Política de Privacidad", href: "/privacy-policy" },
          { title: "Términos de Uso", href: "/terms-of-use" },
          { title: "Política de Reembolso", href: "/refund-policy" },
          {
            title: "Eliminación de Clientes",
            href: "/customer-deletion-policy",
          },
        ],
        resourceLinks: [
          { title: "Tutoriales en Video", href: "/tutorials" },
          { title: "Preguntas Frecuentes", href: "/faqs" },
          { title: "Blogs", href: "/blogs" },
        ],
        helpLinks: [
          { title: "Contáctenos", href: "/#contact-us" },
        ],
        shippingLinks: [
          { title: "Calculadora de Envío", href: "/#shipping_calculator" },
          { title: "Nuestras Ubicaciones", href: "/#select_locations" },
          { title: "No se Puede Enviar", href: "/shipping-restrictions" },
        ],
        paymentLinks: [
          { title: "Métodos de Pago", href: "/#payment_methods_section" },
        ],
        planLinks: [
          { title: "Cómo Funciona", href: "/#how_it_works_section" },
          { title: "Características", href: "/#features_section" },
        ],
      };

    default: // "en"
      return {
        navGroups: {
          platform: "Platform",
          resource: "Resource",
          carriers: "Carriers",
          pricing: "Pricing",
        },
        sections: {
          company: "Company",
          app: "App",
          learn: "Learn",
          help: "Help",
          shipping: "Shipping",
          payments: "Payments",
          plans: "Plans",
          legal: "Legal",
        },
        exploreLinks: {
          main: {
            left: [
              { title: "Download Baskitt App", href: "/#hero_section" },
              { title: "Why Us", href: "/#features_section" },
              { title: "About Us", href: "/#about_us_section" },
              { title: "Most Use", href: "/#most_use_section" },
              { title: "Key Features", href: "/#key_features_section" },
            ],
            right: [
              { title: "Tutorials", href: "/#product_tutorials" },
              { title: "Integration", href: "/#integration" },
              { title: "Zones", href: "/#located_section" },
              { title: "Contact Us", href: "/#contact-us" },
            ],
          },
          subdomain: {
            left: [
              { title: "Download Baskitt App", href: "/#hero_section" },
              { title: "Why Us", href: "/#why_us_section" },
              { title: "Shipping Calculator", href: "/#shipping_calculator" },
              { title: "Our Locations", href: "/#select_locations" },
              { title: "App Features", href: "/#app_features_section" },
            ],
            right: [
              { title: "How It Works", href: "/#how_it_works_section" },
              { title: "Key Features", href: "/#features_section" },
              { title: "Payment Methods", href: "/#payment_methods_section" },
              { title: "Contact Us", href: "/#contact-us" },
            ],
          },
        },
        legalLinks: [
          { title: "Privacy Policy", href: "/privacy-policy" },
          { title: "Terms of Use", href: "/terms-of-use" },
          { title: "Refund Policy", href: "/refund-policy" },
          { title: "Customer Deletion", href: "/customer-deletion-policy" },
        ],
        resourceLinks: [
          { title: "Video Tutorials", href: "/tutorials" },
          { title: "Testimonials", href: "/#testimonials" },
          { title: "FAQs", href: "/faqs" },
          { title: "Blogs", href: "/blogs" },
        ],
        helpLinks: [
          { title: "Contact Us", href: "/#contact-us" },
        ],
        shippingLinks: [
          { title: "Can't Be Shipped", href: "/shipping-restrictions" },
        ],
        paymentLinks: [],
        planLinks: [
          { title: "How It Works", href: "/#how_it_works_section" },
          { title: "Features", href: "/#features_section" },
        ],
      };
  }
};

// ─── Build NAV_GROUPS from locale + subdomain ─────────────────────────────────
// Mirrors the footer's subdomain detection: if subdomain exists, use subdomain
// links for Platform; otherwise use main links. All other groups are universal.

const buildNavGroups = (t, isSubdomain) => {
  const explore = isSubdomain ? t.exploreLinks.subdomain : t.exploreLinks.main;

  // Platform group: left column = company/app split, right column = explore right links
  // We split explore.left into two groups to match the original two-section pattern
  const platformLeftHalf = explore.left.slice(
    0,
    Math.ceil(explore.left.length / 2),
  );
  const platformRightHalf = explore.left.slice(
    Math.ceil(explore.left.length / 2),
  );

  return [
    {
      id: "platform",
      label: t.navGroups.platform,
      sections: [
        {
          heading: t.sections.company,
          links: platformLeftHalf,
        },
        {
          heading: t.sections.app,
          links: platformRightHalf,
        },
      ],
    },
    {
      id: "resource",
      label: t.navGroups.resource,
      sections: [
        {
          heading: t.sections.learn,
          links: t.resourceLinks,
        },
        {
          heading: t.sections.help,
          links: t.helpLinks,
        },
      ],
    },
    {
      id: "carriers",
      label: t.navGroups.carriers,
      sections: [
        {
          heading: t.sections.shipping,
          links: t.shippingLinks,
        },
        ...(t.paymentLinks.length > 0
          ? [
              {
                heading: t.sections.payments,
                links: t.paymentLinks,
              },
            ]
          : []),
      ],
    },
    {
      id: "pricing",
      label: t.navGroups.pricing,
      fromRight: true,
      sections: [
        {
          heading: t.sections.legal,
          links: t.legalLinks,
        },
      ],
    },
  ];
};

// ─── Build MOBILE_SECTIONS from locale + subdomain ────────────────────────────

const buildMobileSections = (t, isSubdomain) => {
  const explore = isSubdomain ? t.exploreLinks.subdomain : t.exploreLinks.main;
  const allExploreLinks = [...explore.left, ...explore.right];

  return [
    {
      title: t.navGroups.platform,
      links: allExploreLinks,
    },
    {
      title: t.navGroups.resource,
      links: [...t.resourceLinks, ...t.helpLinks],
    },
    {
      title: t.navGroups.carriers,
      links: [...t.shippingLinks, ...t.paymentLinks],
    },
    {
      title: `${t.navGroups.pricing} & ${t.sections.legal}`,
      links: [...t.planLinks, ...t.legalLinks],
    },
  ];
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [openId, setOpenId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHero, setIsHero] = useState(true);
  const [lang, setLang] = useState("en");
  const [isSubdomain, setIsSubdomain] = useState(false);

  const heroBottomRef = useRef(null);
  const tickingRef = useRef(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    setLang(Cookies.get("language") || "en");

    // Match footer subdomain detection logic exactly
    const host = window.location.hostname;
    const parts = host.split(".");
    setIsSubdomain(parts.length > 1 && parts[0] !== "www");

    const hero = document.getElementById("hero_section");
    const calc = () => {
      if (!hero) return;
      heroBottomRef.current =
        hero.getBoundingClientRect().bottom + window.scrollY;
    };
    calc();

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 50);
        if (heroBottomRef.current != null) setIsHero(y < heroBottomRef.current);
        tickingRef.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", calc);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", calc);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleLangChange = (code) => {
    setLang(code);
    Cookies.set("language", code, { expires: 365 });
    window.location.reload();
  };

  const openMenu = (id) => {
    clearTimeout(closeTimer.current);
    setOpenId(id);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenId(null), 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  // ── Derive locale content + build groups ──
  const t = getLocaleContent(lang);
  const NAV_GROUPS = buildNavGroups(t, isSubdomain);
  const MOBILE_SECTIONS = buildMobileSections(t, isSubdomain);

  // RTL support
  const isRtl = lang === "ar-IQ" || lang === "ckb";

  return (
    <>
      <nav
        id="navbar"
        dir={isRtl ? "rtl" : "ltr"}
        className={["navbar", scrolled && "scrolled", !isHero && "navbar-dark"]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Logo */}
        <Link href="/" className="logo_wrapper">
          <Image
            src="/images/logo.png"
            width={152}
            height={52}
            alt="Baskitt"
            priority
            className="logo"
          />
        </Link>

        {/* ── Center pill ── */}
        <div className="nb-pill" role="menubar">
          {NAV_GROUPS.map((group) => (
            <div
              key={group.id}
              className={`nb-item${openId === group.id ? " open" : ""}`}
              onMouseEnter={() => openMenu(group.id)}
              onMouseLeave={scheduleClose}
            >
              <button
                className="nb-btn"
                aria-haspopup="true"
                aria-expanded={openId === group.id}
                onClick={() => setOpenId(openId === group.id ? null : group.id)}
              >
                {group.label}
                <svg
                  viewBox="0 0 10 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M1 1l4 4 4-4" />
                </svg>
              </button>

              <div
                className={`nb-drop${group.fromRight ? " from-right" : ""}`}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                {group.sections.map((sec) => (
                  <div key={sec.heading} className="nb-drop-section">
                    <div className="nb-drop-label">{sec.heading}</div>
                    {sec.links.map((l) => (
                      <Link
                        key={l.href}
                        href={l.href}
                        onClick={() => setOpenId(null)}
                      >
                        {l.title}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right controls */}
        <div className="navbar_right">
          <CountryDropdown isHeroSection={isHero} />
        </div>
      </nav>

      {/* Mobile overlay */}
      <div
        className={`mob-overlay${menuOpen ? " open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile drawer */}
      <div
        className={`mob-drawer${menuOpen ? " open" : ""}`}
        dir={isRtl ? "rtl" : "ltr"}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {MOBILE_SECTIONS.map((sec) => (
          <div key={sec.title}>
            <div className="mob-section-title">{sec.title}</div>
            {sec.links.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="mob-link"
                onClick={() => setMenuOpen(false)}
              >
                {l.title}
              </Link>
            ))}
          </div>
        ))}

        {/* Language + country in drawer */}
        <div className="mob-bottom">
          <CountryDropdown isHeroSection={false} fullWidth />
        </div>
      </div>

      <button
        className={`nb-ham${menuOpen ? " open" : ""} ${
          scrolled ? "scrolled" : ""
        }`}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
      >
        <span />
        <span />
        <span />
      </button>
    </>
  );
}
